import React, { useState } from 'react';
import { defaultPurposeSplit, PURPOSE_BANDS } from './tripPurpose';

const DEFAULT_TIME_PERIODS = [
  { id: 'tp1', name: 'AM Peak', startTime: '08:00' },
  { id: 'tp2', name: 'PM Peak', startTime: '17:00' },
];

const DEFAULT_SCENARIO = (id, name) => ({
  id,
  name,
  devSize: '',
  tripEntryMode: 'rates', // 'rates' | 'direct'
  tripRates: {},
  directTrips: {}, // { [timePeriodId]: { arrivals: '', departures: '' } }
  // Trip purpose split per period: { [tpId]: { employment, education, other } }
  // (whole %s). Empty = use the NTS default seeded from the period's start time.
  tripPurpose: {},
  purposeBand: 'hour', // 'hour' | 'broad' — which NTS table seeds the defaults

  // Scenario 2 matching options
  sameDevSize: false,
  sameTripRates: false,
  sameDistribution: false,
  sameModeShare: false,
  sameTripPurpose: false,
});

function inputStyle(highlight = false) {
  return {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1.5px solid ${highlight ? '#3D1A6E' : '#EDE5F8'}`,
    fontSize: 13, fontFamily: 'inherit', color: '#1C1C2E',
    outline: 'none', boxSizing: 'border-box', background: '#FAFAFA'
  };
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>
      {children}
    </div>
  );
}

function SectionHeader({ number, title, subtitle }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#3D1A6E', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          {number}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1C1C2E' }}>{title}</div>
      </div>
      {subtitle && <div style={{ fontSize: 13, color: '#6B6880', marginLeft: 40 }}>{subtitle}</div>}
    </div>
  );
}

function MatchToggle({ label, checked, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!checked)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, border: `1.5px solid ${checked ? '#3D1A6E' : '#EDE5F8'}`, background: checked ? '#F5F0FA' : 'white', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, marginBottom: 8 }}>
      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? '#3D1A6E' : '#C4BDD4'}`, background: checked ? '#3D1A6E' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {checked && <div style={{ color: 'white', fontSize: 11, lineHeight: 1, fontWeight: 700 }}>✓</div>}
      </div>
      <div style={{ fontSize: 12, color: checked ? '#3D1A6E' : '#6B6880', fontWeight: checked ? 700 : 400 }}>{label}</div>
    </div>
  );
}

// Trip-purpose split editor for one scenario: per period, three % inputs
// (Employment / Education / Other) seeded from the NTS default for that period's
// start time. Education is called out because it drives the school gravity model
// downstream; the others keep the census distribution + mode share.
function PurposeSplitEditor({ scenarioIndex, scenario, timePeriods, onChange, onBandChange }) {
  const PURP = [
    { key: 'employment', label: 'Employment', color: '#3D1A6E', hint: 'commuting + business' },
    { key: 'education', label: 'Education', color: '#0D9488', hint: 'incl. school run — distributed to schools' },
    { key: 'other', label: 'Other', color: '#7B4DBF', hint: 'shopping, leisure, etc.' },
  ];
  const band = scenario.purposeBand || 'hour';
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 4 }}>Trip Purpose Split</div>
      <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 10 }}>
        Splits person trips by purpose (National Travel Survey defaults, seeded from each period's start time — editable).
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Label>NTS band</Label>
        <select value={band} onChange={e => onBandChange(scenarioIndex, e.target.value)}
          style={{ ...inputStyle(), maxWidth: 280, padding: '6px 8px', cursor: 'pointer' }}>
          {PURPOSE_BANDS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <span style={{ fontSize: 10, color: '#6B6880' }}>reseeds unedited periods</span>
      </div>
      {timePeriods.map(tp => {
        const stored = scenario.tripPurpose?.[tp.id];
        const split = stored || defaultPurposeSplit(tp.startTime, band);
        const isDefault = !stored;
        const total = ['employment', 'education', 'other']
          .reduce((s, k) => s + (parseFloat(split[k]) || 0), 0);
        const totalOk = Math.abs(total - 100) < 0.05;
        return (
          <div key={tp.id} style={{ background: '#F9F7FD', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E' }}>
                {tp.name || 'Unnamed period'} <span style={{ fontWeight: 400, color: '#6B6880' }}>· {tp.startTime}</span>
                {isDefault && <span style={{ fontWeight: 600, color: '#0D9488', marginLeft: 8, fontSize: 10 }}>NTS default</span>}
              </div>
              {!isDefault && (
                <button onClick={() => onChange(scenarioIndex, tp.id, null)}
                  style={{ background: 'none', border: 'none', color: '#7B4DBF', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
                  Reset to NTS
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {PURP.map(p => (
                <div key={p.key} style={{ flex: 1 }}>
                  <Label>{p.label}</Label>
                  <div style={{ position: 'relative' }}>
                    <input type="number" step="0.1" min="0" max="100" value={split[p.key]}
                      onChange={e => onChange(scenarioIndex, tp.id, { ...split, [p.key]: e.target.value })}
                      style={{ ...inputStyle(), paddingRight: 22 }} />
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#6B6880' }}>%</span>
                  </div>
                  <div style={{ fontSize: 9, color: p.color, marginTop: 3, fontWeight: 600 }}>{p.hint}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, marginTop: 8, fontWeight: 700, color: totalOk ? '#0D9488' : '#C2410C' }}>
              Total: {total.toFixed(1)}%{totalOk ? ' ✓' : ' — will be normalised to 100%'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ProjectSetup({ initialTimePeriods, initialScenarios, onComplete, onSkip, isEditing }) {
  const [timePeriods, setTimePeriods] = useState(
    initialTimePeriods && initialTimePeriods.length > 0 ? initialTimePeriods : DEFAULT_TIME_PERIODS
  );
  const [scenarios, setScenarios] = useState(
    initialScenarios && initialScenarios.length > 0 ? initialScenarios : [DEFAULT_SCENARIO('s1', 'Scenario 1')]
  );
  const [activeScenario, setActiveScenario] = useState(0);

  // ── Time period helpers ──
  function addTimePeriod() {
    if (timePeriods.length >= 3) return;
    const id = `tp${Date.now()}`;
    setTimePeriods(prev => [...prev, { id, name: '', startTime: '12:00' }]);
  }

  function removeTimePeriod(id) {
    if (timePeriods.length <= 1) return;
    setTimePeriods(prev => prev.filter(tp => tp.id !== id));
    // Clean up trip rates for removed period
    setScenarios(prev => prev.map(sc => {
      const newRates = { ...sc.tripRates };
      delete newRates[id];
      return { ...sc, tripRates: newRates };
    }));
  }

  function updateTimePeriod(id, field, value) {
    setTimePeriods(prev => prev.map(tp => tp.id === id ? { ...tp, [field]: value } : tp));
  }

  // ── Scenario helpers ──
  function addScenario() {
    setActiveScenario(scenarios.length); // new scenario's index
    setScenarios(prev => [...prev, DEFAULT_SCENARIO(`s${Date.now()}`, `Scenario ${prev.length + 1}`)]);
  }

  function removeScenario(index) {
    if (index === 0) return;
    setScenarios(prev => prev.filter((_, i) => i !== index));
    setActiveScenario(0);
  }

  function updateScenario(index, field, value) {
    setScenarios(prev => prev.map((sc, i) => i === index ? { ...sc, [field]: value } : sc));
  }

  function updateDirectTrips(scenarioIndex, timePeriodId, field, value) {
    setScenarios(prev => prev.map((sc, i) => {
      if (i !== scenarioIndex) return sc;
      return {
        ...sc,
        directTrips: {
          ...sc.directTrips,
          [timePeriodId]: {
            ...(sc.directTrips[timePeriodId] || { arrivals: '', departures: '' }),
            [field]: value
          }
        }
      };
    }));
  }

  function updateTripPurpose(scenarioIndex, timePeriodId, split) {
    setScenarios(prev => prev.map((sc, i) => {
      if (i !== scenarioIndex) return sc;
      return {
        ...sc,
        tripPurpose: { ...sc.tripPurpose, [timePeriodId]: split },
      };
    }));
  }

  function updateTripRate(scenarioIndex, timePeriodId, field, value) {
    setScenarios(prev => prev.map((sc, i) => {
      if (i !== scenarioIndex) return sc;
      return {
        ...sc,
        tripRates: {
          ...sc.tripRates,
          [timePeriodId]: {
            ...(sc.tripRates[timePeriodId] || { arrivals: '', departures: '' }),
            [field]: value
          }
        }
      };
    }));
  }

  function handleComplete() {
    onComplete({ timePeriods, scenarios });
  }

  const s1 = scenarios[0];

  return (
    <div style={{ minHeight: '100vh', background: '#F6F1E9', fontFamily: 'Nunito Sans, Avenir, sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1.5px solid #EDE5F8', borderLeft: '4px solid #3D1A6E', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56, boxShadow: '0 2px 8px rgba(61,26,110,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="18" height="22" viewBox="0 0 197 249" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M158.29,177.25c4.37,3.62,3.99,11.25,1.09,14.9-3.48,4.37-9.56,4.58-13.73,1.1l-48.6-40.52-.24,71.04c-.02,5.58-6.19,8.2-10.14,7.99-5.27-.27-9.86-3.75-9.89-9.84l-.29-69.51-47.82,41.21c-4.27,3.68-9.47,3.15-13.56-.41-2.97-2.58-4.95-10.22-.59-13.94l56.23-47.93L15.15,85.99c-4.5-3.67-3.42-10.67-.58-13.96,3.79-4.38,9.86-4.61,14.39-.94l47.62,38.59.09-91.63c0-4.41,1.79-8.05,5.23-9.8,2.94-1.5,7.95-1.51,10.98.9l29.47,23.46c5.92,4.72,10.1,11.62,4.75,18.78-3.06,4.09-9.99,3.9-14.06.58l-16.07-13.13v70.41s46.81-40.03,46.81-40.03l-12.93-12.02,31.27-6.11,22.14-4.2-17.67,53.75-8.72-17.32-55.58,47.57,56,46.38Z" fill="#3D1A6E"/>
          </svg>
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', color: '#3D1A6E' }}>ODINFLOW</span>
          <div style={{ width: 1, height: 20, background: '#EDE5F8', margin: '0 8px' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6B6880' }}>{isEditing ? 'Edit Project Setup' : 'Project Setup'}</span>
        </div>
        {onSkip && (
          <button onClick={onSkip}
            style={{ background: 'white', border: '1.5px solid #EDE5F8', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 700, color: '#6B6880', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
            {isEditing ? 'Cancel' : 'Skip for now →'}
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '40px 0' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>

          {!isEditing && (
            <div style={{ marginBottom: 40 }}>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1C1C2E', margin: 0, marginBottom: 8 }}>Set up your assessment</h1>
              <p style={{ fontSize: 14, color: '#6B6880', margin: 0 }}>Define your time periods and scenarios before starting. You can edit these later.</p>
            </div>
          )}

          {/* ── Section 1: Time Periods ── */}
          <div style={{ background: 'white', borderRadius: 10, padding: 28, marginBottom: 20, boxShadow: '0 2px 12px rgba(61,26,110,0.06)', border: '1.5px solid #EDE5F8' }}>
            <SectionHeader number="1" title="Time Periods" subtitle="Define the peak periods for your assessment (up to 3)." />

            {timePeriods.map((tp, i) => (
              <div key={tp.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12, background: '#F9F7FD', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ flex: 2 }}>
                  <Label>Period Name</Label>
                  <input value={tp.name} onChange={e => updateTimePeriod(tp.id, 'name', e.target.value)}
                    placeholder="e.g. AM Peak" style={inputStyle()} />
                </div>
                <div style={{ flex: 1 }}>
                  <Label>Start Time</Label>
                  <input type="time" value={tp.startTime} onChange={e => updateTimePeriod(tp.id, 'startTime', e.target.value)}
                    style={inputStyle()} />
                </div>
                <button onClick={() => removeTimePeriod(tp.id)} disabled={timePeriods.length <= 1}
                  style={{ padding: '8px 10px', background: timePeriods.length <= 1 ? '#F5F0FA' : '#FEE2E2', color: timePeriods.length <= 1 ? '#C4BDD4' : '#991B1B', border: 'none', borderRadius: 6, cursor: timePeriods.length <= 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: 'inherit', flexShrink: 0 }}>
                  ✕
                </button>
              </div>
            ))}

            {timePeriods.length < 3 && (
              <button onClick={addTimePeriod}
                style={{ width: '100%', padding: '9px', background: 'white', color: '#3D1A6E', border: '1.5px dashed #C4BDD4', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.06em' }}>
                + Add time period
              </button>
            )}
          </div>

          {/* ── Section 2: Scenarios ── */}
          <div style={{ background: 'white', borderRadius: 10, padding: 28, marginBottom: 28, boxShadow: '0 2px 12px rgba(61,26,110,0.06)', border: '1.5px solid #EDE5F8' }}>
            <SectionHeader number="2" title="Scenarios" subtitle="Define your assessment scenarios (e.g. BAU, Decide & Provide). Add as many as you need." />

            {/* Scenario tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {scenarios.map((sc, i) => (
                <button key={sc.id} onClick={() => setActiveScenario(i)}
                  style={{ padding: '8px 18px', background: activeScenario === i ? '#3D1A6E' : 'white', color: activeScenario === i ? 'white' : '#3D1A6E', border: `1.5px solid ${activeScenario === i ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {sc.name || `Scenario ${i + 1}`}
                </button>
              ))}
              <button onClick={addScenario}
                style={{ padding: '8px 18px', background: 'white', color: '#7B4DBF', border: '1.5px dashed #C4BDD4', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                + Add scenario
              </button>
              {activeScenario > 0 && (
                <button onClick={() => removeScenario(activeScenario)}
                  style={{ marginLeft: 'auto', padding: '8px 14px', background: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Remove {scenarios[activeScenario]?.name || 'scenario'}
                </button>
              )}
            </div>

            {/* Scenario 1 */}
            {activeScenario === 0 && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <Label>Scenario Name</Label>
                  <input value={s1.name} onChange={e => updateScenario(0, 'name', e.target.value)}
                    placeholder="e.g. Base Case" style={inputStyle(true)} />
                </div>

                {/* Trip entry mode toggle */}
                <div style={{ marginBottom: 16 }}>
                  <Label>Trip Generation Method</Label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[{ key: 'rates', label: '📊 Trip rates (per unit)' }, { key: 'direct', label: '✏️ Direct trip entry' }].map(m => (
                      <button key={m.key} onClick={() => updateScenario(0, 'tripEntryMode', m.key)}
                        style={{ flex: 1, padding: '8px 10px', background: s1.tripEntryMode === m.key ? '#3D1A6E' : 'white', color: s1.tripEntryMode === m.key ? 'white' : '#3D1A6E', border: `1.5px solid ${s1.tripEntryMode === m.key ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dev size — always editable, label notes when not used for calculation */}
                <div style={{ marginBottom: 20 }}>
                  <Label>Development Size {s1.tripEntryMode === 'direct' ? '(for reference — trips entered directly)' : ''}</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="number" value={s1.devSize} onChange={e => updateScenario(0, 'devSize', e.target.value)}
                      placeholder="e.g. 100"
                      style={{ ...inputStyle(), maxWidth: 160 }} />
                    <span style={{ fontSize: 12, color: '#6B6880' }}>units / dwellings</span>
                  </div>
                </div>

                {s1.tripEntryMode === 'rates' ? (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 12 }}>Trip Rates (per unit)</div>
                    {timePeriods.map(tp => {
                      const rates = s1.tripRates[tp.id] || { arrivals: '', departures: '' };
                      const size = parseFloat(s1.devSize) || 0;
                      const arrTrips = size > 0 && rates.arrivals ? Math.round(parseFloat(rates.arrivals) * size) : null;
                      const depTrips = size > 0 && rates.departures ? Math.round(parseFloat(rates.departures) * size) : null;
                      return (
                        <div key={tp.id} style={{ background: '#F9F7FD', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', marginBottom: 10 }}>
                            {tp.name || 'Unnamed period'} <span style={{ fontWeight: 400, color: '#6B6880' }}>· {tp.startTime}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <Label>Arrivals</Label>
                              <input type="number" step="0.01" value={rates.arrivals}
                                onChange={e => updateTripRate(0, tp.id, 'arrivals', e.target.value)}
                                placeholder="e.g. 0.30" style={inputStyle()} />
                              {arrTrips !== null && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 3, fontWeight: 600 }}>{arrTrips} arrival trips</div>}
                            </div>
                            <div style={{ flex: 1 }}>
                              <Label>Departures</Label>
                              <input type="number" step="0.01" value={rates.departures}
                                onChange={e => updateTripRate(0, tp.id, 'departures', e.target.value)}
                                placeholder="e.g. 0.20" style={inputStyle()} />
                              {depTrips !== null && <div style={{ fontSize: 10, color: '#7B4DBF', marginTop: 3, fontWeight: 600 }}>{depTrips} departure trips</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 12 }}>Direct Trip Entry</div>
                    {timePeriods.map(tp => {
                      const direct = s1.directTrips?.[tp.id] || { arrivals: '', departures: '' };
                      return (
                        <div key={tp.id} style={{ background: '#F9F7FD', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', marginBottom: 10 }}>
                            {tp.name || 'Unnamed period'} <span style={{ fontWeight: 400, color: '#6B6880' }}>· {tp.startTime}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <Label>Arrivals (vehicle trips)</Label>
                              <input type="number" step="1" value={direct.arrivals}
                                onChange={e => updateDirectTrips(0, tp.id, 'arrivals', e.target.value)}
                                placeholder="e.g. 125" style={inputStyle()} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <Label>Departures (vehicle trips)</Label>
                              <input type="number" step="1" value={direct.departures}
                                onChange={e => updateDirectTrips(0, tp.id, 'departures', e.target.value)}
                                placeholder="e.g. 63" style={inputStyle()} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                <PurposeSplitEditor scenarioIndex={0} scenario={s1} timePeriods={timePeriods} onChange={updateTripPurpose} onBandChange={(i, b) => updateScenario(i, 'purposeBand', b)} />
              </div>
            )}

            {/* Scenario N (index > 0) — can match against the first scenario */}
            {activeScenario > 0 && scenarios[activeScenario] && (() => {
              const ai = activeScenario;
              const s2 = scenarios[ai];
              return (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <Label>Scenario Name</Label>
                  <input value={s2.name} onChange={e => updateScenario(ai, 'name', e.target.value)}
                    placeholder="e.g. Decide & Provide" style={inputStyle(true)} />
                </div>

                <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 12 }}>
                    Match with {s1.name || 'Scenario 1'}
                  </div>
                  <MatchToggle label={`Same development size as ${s1.name || 'Scenario 1'} (${s1.devSize || '—'} units)`}
                    checked={s2.sameDevSize} onChange={v => updateScenario(ai,'sameDevSize', v)} />
                  <MatchToggle label={`Same trip rates as ${s1.name || 'Scenario 1'}`}
                    checked={s2.sameTripRates} onChange={v => updateScenario(ai,'sameTripRates', v)} />
                  <MatchToggle label={`Same trip distribution as ${s1.name || 'Scenario 1'}`}
                    checked={s2.sameDistribution} onChange={v => updateScenario(ai,'sameDistribution', v)} />
                  <MatchToggle label={`Same mode share as ${s1.name || 'Scenario 1'}`}
                    checked={s2.sameModeShare} onChange={v => updateScenario(ai,'sameModeShare', v)} />
                  <MatchToggle label={`Same trip purpose split as ${s1.name || 'Scenario 1'}`}
                    checked={s2.sameTripPurpose} onChange={v => updateScenario(ai,'sameTripPurpose', v)} />
                </div>

                {/* Dev size — show if not matching */}
                {!s2.sameDevSize && (
                  <div style={{ marginBottom: 20 }}>
                    <Label>Development Size</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="number" value={s2.devSize} onChange={e => updateScenario(ai,'devSize', e.target.value)}
                        placeholder="e.g. 150" style={{ ...inputStyle(), maxWidth: 160 }} />
                      <span style={{ fontSize: 12, color: '#6B6880' }}>units / dwellings</span>
                    </div>
                  </div>
                )}

                {/* Trip rates — show if not matching */}
                {!s2.sameTripRates && (
                  <>
                    {/* Trip entry mode toggle */}
                    <div style={{ marginBottom: 16 }}>
                      <Label>Trip Generation Method</Label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[{ key: 'rates', label: '📊 Trip rates (per unit)' }, { key: 'direct', label: '✏️ Direct trip entry' }].map(m => (
                          <button key={m.key} onClick={() => updateScenario(ai,'tripEntryMode', m.key)}
                            style={{ flex: 1, padding: '8px 10px', background: s2.tripEntryMode === m.key ? '#3D1A6E' : 'white', color: s2.tripEntryMode === m.key ? 'white' : '#3D1A6E', border: `1.5px solid ${s2.tripEntryMode === m.key ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {s2.tripEntryMode === 'rates' ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 12 }}>Trip Rates (per unit)</div>
                        {timePeriods.map(tp => {
                          const rates = s2.tripRates[tp.id] || { arrivals: '', departures: '' };
                          const size = s2.sameDevSize ? parseFloat(s1.devSize) || 0 : parseFloat(s2.devSize) || 0;
                          const arrTrips = size > 0 && rates.arrivals ? Math.round(parseFloat(rates.arrivals) * size) : null;
                          const depTrips = size > 0 && rates.departures ? Math.round(parseFloat(rates.departures) * size) : null;
                          return (
                            <div key={tp.id} style={{ background: '#F9F7FD', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', marginBottom: 10 }}>
                                {tp.name || 'Unnamed period'} <span style={{ fontWeight: 400, color: '#6B6880' }}>· {tp.startTime}</span>
                              </div>
                              <div style={{ display: 'flex', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                  <Label>Arrivals</Label>
                                  <input type="number" step="0.01" value={rates.arrivals}
                                    onChange={e => updateTripRate(ai,tp.id, 'arrivals', e.target.value)}
                                    placeholder="e.g. 0.30" style={inputStyle()} />
                                  {arrTrips !== null && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 3, fontWeight: 600 }}>{arrTrips} arrival trips</div>}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <Label>Departures</Label>
                                  <input type="number" step="0.01" value={rates.departures}
                                    onChange={e => updateTripRate(ai,tp.id, 'departures', e.target.value)}
                                    placeholder="e.g. 0.20" style={inputStyle()} />
                                  {depTrips !== null && <div style={{ fontSize: 10, color: '#7B4DBF', marginTop: 3, fontWeight: 600 }}>{depTrips} departure trips</div>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 12 }}>Direct Trip Entry</div>
                        {timePeriods.map(tp => {
                          const direct = s2.directTrips?.[tp.id] || { arrivals: '', departures: '' };
                          return (
                            <div key={tp.id} style={{ background: '#F9F7FD', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', marginBottom: 10 }}>
                                {tp.name || 'Unnamed period'} <span style={{ fontWeight: 400, color: '#6B6880' }}>· {tp.startTime}</span>
                              </div>
                              <div style={{ display: 'flex', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                  <Label>Arrivals (vehicle trips)</Label>
                                  <input type="number" step="1" value={direct.arrivals}
                                    onChange={e => updateDirectTrips(ai,tp.id, 'arrivals', e.target.value)}
                                    placeholder="e.g. 125" style={inputStyle()} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <Label>Departures (vehicle trips)</Label>
                                  <input type="number" step="1" value={direct.departures}
                                    onChange={e => updateDirectTrips(ai,tp.id, 'departures', e.target.value)}
                                    placeholder="e.g. 63" style={inputStyle()} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </>
                )}

                {/* Summary if matching */}
                {s2.sameTripRates && (
                  <div style={{ background: '#F0FDF9', borderRadius: 8, padding: '12px 14px', marginBottom: 16, border: '1px solid #0D9488' }}>
                    <div style={{ fontSize: 12, color: '#0D9488', fontWeight: 600 }}>✅ Using same trip rates as {s1.name || 'Scenario 1'}</div>
                  </div>
                )}

                {/* Trip purpose split — show unless matching Scenario 1 */}
                {s2.sameTripPurpose ? (
                  <div style={{ background: '#F0FDF9', borderRadius: 8, padding: '12px 14px', marginTop: 4, border: '1px solid #0D9488' }}>
                    <div style={{ fontSize: 12, color: '#0D9488', fontWeight: 600 }}>✅ Using same trip purpose split as {s1.name || 'Scenario 1'}</div>
                  </div>
                ) : (
                  <PurposeSplitEditor scenarioIndex={ai} scenario={s2} timePeriods={timePeriods} onChange={updateTripPurpose} onBandChange={(i, b) => updateScenario(i, 'purposeBand', b)} />
                )}
              </div>
              );
            })()}
          </div>

          {/* ── Summary card ── */}
          <div style={{ background: '#3D1A6E', borderRadius: 10, padding: '20px 24px', marginBottom: 28 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#C4BDD4', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Assessment Summary</div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, color: '#C4BDD4', marginBottom: 4 }}>Time Periods</div>
                {timePeriods.map(tp => (
                  <div key={tp.id} style={{ fontSize: 13, color: 'white', fontWeight: 600 }}>{tp.name || '—'} <span style={{ fontWeight: 400, color: '#C4BDD4' }}>({tp.startTime})</span></div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#C4BDD4', marginBottom: 4 }}>Scenarios</div>
                {scenarios.map((sc, i) => (
                  <div key={sc.id} style={{ fontSize: 13, color: 'white', fontWeight: 600 }}>
                    {sc.name || `Scenario ${i + 1}`}
                    {sc.devSize && <span style={{ fontWeight: 400, color: '#C4BDD4' }}> · {sc.devSize} units</span>}
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#C4BDD4', marginBottom: 4 }}>Total Combinations</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>{timePeriods.length * scenarios.length}</div>
                <div style={{ fontSize: 11, color: '#C4BDD4' }}>period × scenario</div>
              </div>
            </div>
          </div>

          {/* ── Actions ── */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            {onSkip && !isEditing && (
              <button onClick={onSkip}
                style={{ padding: '12px 24px', background: 'white', color: '#6B6880', border: '1.5px solid #EDE5F8', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Skip for now
              </button>
            )}
            {isEditing && (
              <button onClick={onSkip}
                style={{ padding: '12px 24px', background: 'white', color: '#6B6880', border: '1.5px solid #EDE5F8', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
            )}
            <button onClick={handleComplete}
              style={{ padding: '12px 32px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.04em' }}>
              {isEditing ? '💾 Save Setup' : 'Continue to Map →'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
